/* eslint-disable no-param-reassign */
import cn from 'classnames'
import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import WhoIsTyping from 'matrix-react-sdk/lib/WhoIsTyping'
import Timer from 'matrix-react-sdk/lib/utils/Timer'
import sdk from 'matrix-react-sdk'
import MatrixClientPeg from 'matrix-react-sdk/lib//MatrixClientPeg'
import { getUserType } from 'ducks/chat/utils/users'
import { uniq } from 'util/arrays'

export default class WhoIsTypingTile extends PureComponent {
  static propTypes = {
    // the room this statusbar is representing.
    // eslint-disable-next-line react/forbid-prop-types
    room: PropTypes.object.isRequired,
    onShown: PropTypes.func,
    onHidden: PropTypes.func,
  }

  static defaultProps = {
    onShown: null,
    onHidden: null,
  }

  state = {
    usersTyping: WhoIsTyping.usersTypingApartFromMe(this.props.room),
    // a map with userid => Timer to delay
    // hiding the "x is typing" message for a
    // user so hiding it can coincide
    // with the sent message by the other side
    // resulting in less timeline jumpiness
    delayedStopTypingTimers: {},
  }

  componentDidMount() {
    MatrixClientPeg.get().on('RoomMember.typing', this.onRoomMemberTyping)
    MatrixClientPeg.get().on('Room.timeline', this.onRoomTimeline)
  }

  componentDidUpdate(_, prevState) {
    const { onShown, onHidden } = this.props
    const wasVisible = this._isVisible(prevState)
    const isVisible = this._isVisible(this.state)
    if (onShown && !wasVisible && isVisible) {
      onShown()
    } else if (onHidden && wasVisible && !isVisible) {
      onHidden()
    }
  }

  componentWillUnmount() {
    // we may have entirely lost our client as we're logging out before clicking login on the guest bar...
    const client = MatrixClientPeg.get()
    if (client) {
      client.removeListener('RoomMember.typing', this.onRoomMemberTyping)
      client.removeListener('Room.timeline', this.onRoomTimeline)
    }
    Object.values(this.state.delayedStopTypingTimers).forEach(t => t.abort())
  }

  onRoomTimeline = (event, room) => {
    if (room && room.roomId === this.props.room.roomId) {
      const userId = event.getSender()
      // remove user from usersTyping
      const usersTyping = this.state.usersTyping.filter(
        m => m.userId !== userId
      )
      this.setState({ usersTyping })
      // abort timer if any
      this._abortUserTimer(userId)
    }
  }

  onRoomMemberTyping = () => {
    const usersTyping = WhoIsTyping.usersTypingApartFromMeAndIgnored(
      this.props.room
    )
    this.setState({
      delayedStopTypingTimers: this._updateDelayedStopTypingTimers(usersTyping),
      usersTyping,
    })
  }

  _isVisible(state) {
    return (
      state.usersTyping.length !== 0 ||
      Object.keys(state.delayedStopTypingTimers).length !== 0
    )
  }

  isVisible() {
    return this._isVisible(this.state)
  }

  _updateDelayedStopTypingTimers(usersTyping) {
    const usersThatStoppedTyping = this.state.usersTyping.filter(a => {
      return !usersTyping.some(b => a.userId === b.userId)
    })
    const usersThatStartedTyping = usersTyping.filter(a => {
      return !this.state.usersTyping.some(b => a.userId === b.userId)
    })
    // abort all the timers for the users that started typing again
    usersThatStartedTyping.forEach(m => {
      const timer = this.state.delayedStopTypingTimers[m.userId]
      if (timer) {
        timer.abort()
      }
    })
    // prepare new delayedStopTypingTimers object to update state with
    let delayedStopTypingTimers = Object.assign(
      {},
      this.state.delayedStopTypingTimers
    )
    // remove members that started typing again
    delayedStopTypingTimers = usersThatStartedTyping.reduce(
      (delayedStopTypingTimersInner, m) => {
        delete delayedStopTypingTimersInner[m.userId]
        return delayedStopTypingTimersInner
      },
      delayedStopTypingTimers
    )
    // start timer for members that stopped typing
    delayedStopTypingTimers = usersThatStoppedTyping.reduce(
      (delayedStopTypingTimersInner, m) => {
        if (!delayedStopTypingTimersInner[m.userId]) {
          const timer = new Timer(1000)
          delayedStopTypingTimersInner[m.userId] = timer
          timer.start()
          timer.finished().then(
            () => this._removeUserTimer(m.userId), // on elapsed
            () => {
              /* aborted */
            }
          )
        }
        return delayedStopTypingTimers
      },
      delayedStopTypingTimers
    )

    return delayedStopTypingTimers
  }

  _abortUserTimer(userId) {
    const timer = this.state.delayedStopTypingTimers[userId]
    if (timer) {
      timer.abort()
      this._removeUserTimer(userId)
    }
  }

  _removeUserTimer(userId) {
    const timer = this.state.delayedStopTypingTimers[userId]
    if (timer) {
      const delayedStopTypingTimers = Object.assign(
        {},
        this.state.delayedStopTypingTimers
      )
      delete delayedStopTypingTimers[userId]
      this.setState({ delayedStopTypingTimers })
    }
  }

  _renderTypingIndicatorAvatars(users, limit) {
    let othersCount = 0
    if (users.length > limit) {
      othersCount = users.length - limit + 1
      users = users.slice(0, limit - 1)
    }

    const MemberAvatar = sdk.getComponent('avatars.MemberAvatar')
    const avatars = users.map(u => {
      return (
        <MemberAvatar
          key={u.userId}
          member={u}
          width={32}
          height={32}
          resizeMethod="crop"
          viewUserOnClick={false}
        />
      )
    })

    if (othersCount > 0) {
      avatars.push(
        <span
          className="mx_WhoIsTypingTile_remainingAvatarPlaceholder"
          key="others"
        >
          +{othersCount}
        </span>
      )
    }

    return avatars
  }

  render() {
    const {
      notingUserIds,
      replyingUserIds,
      typingReplyUserIds,
      typingNoteUserIds,
      room,
    } = this.props
    const { usersTyping } = this.state
    const MemberAvatar = sdk.getComponent('avatars.MemberAvatar')

    const replyingUsers = replyingUserIds.map(userId => room.getMember(userId))
    const notingUsers = notingUserIds.map(userId => room.getMember(userId))

    const typingReplyUsers = typingReplyUserIds.map(userId =>
      room.getMember(userId)
    )
    const typingNoteUsers = typingNoteUserIds.map(userId =>
      room.getMember(userId)
    )

    const stoppedUsersOnTimer = Object.keys(
      this.state.delayedStopTypingTimers
    ).map(userId => room.getMember(userId))
    // append the users that have been reported not typing anymore
    // but have a timeout timer running so they can disappear
    // when a message comes in
    let combinedUsersTyping = usersTyping.concat(stoppedUsersOnTimer)

    // Users who we have typing events for in from the groove system
    combinedUsersTyping = combinedUsersTyping.concat(typingReplyUsers)
    combinedUsersTyping = combinedUsersTyping.concat(typingNoteUsers)

    // Users who has the input should also have a typing indicator but without an animation
    combinedUsersTyping = combinedUsersTyping.concat(replyingUsers)
    combinedUsersTyping = combinedUsersTyping.concat(notingUsers)

    // Remove any duplicates, can happen due to us merging matrix and groove typing stats
    combinedUsersTyping = uniq(combinedUsersTyping)

    // In rare cases the member state in indexdb gets corrupted and the user viewing
    // the room isnt actually a member of the room
    combinedUsersTyping = combinedUsersTyping.filter(member => !!member)

    // sort them so the typing members don't change order when
    // moved to delayedStopTypingTimers
    combinedUsersTyping.sort((a, b) => a.name.localeCompare(b.name))

    return (
      <Fragment>
        {combinedUsersTyping.map(member => {
          const { userId } = member
          const isNote =
            notingUsers.some(rm => rm.userId === userId) ||
            typingNoteUsers.some(rm => rm.userId === userId)
          const userType = getUserType(userId)
          const isTyping =
            usersTyping.some(rm => rm.userId === userId) ||
            typingReplyUsers.some(rm => rm.userId === userId) ||
            typingNoteUsers.some(rm => rm.userId === userId)
          const classes = cn({
            mx_WhoIsTypingTile: true,
            mx_EventTile: true,
            [`mx_EventTile_${userType}`]: true,
            mx_EventTile_m_typing: true,
            mx_EventTile_shadow: isNote,
            mx_EventTile_memberAvatar: true,
            mx_IsTyping: isTyping,
          })

          return (
            <li key={userId}>
              <div className={classes} tabIndex={-1}>
                <div className="mx_EventTile_line">
                  <div className="mx_EventTile_content_wrapper">
                    <div className="mx_EventTile_content_container">
                      <div className="mx_EventTile_box">
                        <span>•</span>
                        <span>•</span>
                        <span>•</span>
                      </div>
                    </div>
                    <div className="mx_EventTile_avatar">
                      <MemberAvatar
                        member={member}
                        width={32}
                        height={32}
                        actionText={isTyping ? 'is typing' : 'is replying'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </Fragment>
    )
  }
}
